import { useState, useMemo, useEffect, type ChangeEvent, type ReactNode } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import {
  Plus,
  Search,
  Filter,
  Download,
  Upload,
  Trash2,
  Bookmark,
  BookmarkCheck,
  FileSpreadsheet,
  Columns2,
  Table2,
  ArrowRight,
  X,
} from 'lucide-react';
import api from '../../lib/api';
import { track } from '../../lib/analytics';
import { kolUrlSegment } from '../../lib/slug';
import { resolveMediaUrl } from '../../lib/mediaUrl';
import { kolTotalFollowers } from '../../lib/kolFollowers';
import { KOLProfile, KolGender, Tag } from '../../types';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Select from '../../components/ui/Select';
import Badge from '../../components/ui/Badge';
import DataTable from '../../components/ui/DataTable';
import Modal from '../../components/ui/Modal';
import TagSelector from '../../components/ui/TagSelector';
import { AIKOLDiscoveryPanel } from '../../components/AIKOLDiscoveryPanel';

/** Minimal CSV parse: splits on newlines, handles quoted commas. */
function parseCsvLines(text: string): string[][] {
  const raw = text.trim();
  if (!raw) return [];
  const lines = raw.split(/\r?\n/).filter((l) => l.trim().length > 0);
  return lines.map((line) => {
    const cells: string[] = [];
    let cell = '';
    let inQuotes = false;
    for (let i = 0; i < line.length; i++) {
      const ch = line[i];
      if (ch === '"') {
        inQuotes = !inQuotes;
      } else if (ch === ',' && !inQuotes) {
        cells.push(unquoteCell(cell));
        cell = '';
      } else {
        cell += ch;
      }
    }
    cells.push(unquoteCell(cell));
    return cells;
  });
}

function unquoteCell(s: string): string {
  let t = s.trim();
  if (t.startsWith('"') && t.endsWith('"') && t.length >= 2) {
    t = t.slice(1, -1).replace(/""/g, '"');
  }
  return t;
}

function resolveCsvMapping(headers: string[]) {
  const norm = (s: string) => s.trim().toLowerCase();
  const pickHeader = (candidates: string[]) => {
    for (const c of candidates) {
      const hit = headers.find((h) => norm(h) === norm(c));
      if (hit !== undefined) return hit;
    }
    return null;
  };
  return {
    name: pickHeader(['name', 'display_name']),
    email: pickHeader(['email']),
    phone: pickHeader(['phone']),
  };
}

const KOLS_COLUMNS_STORAGE_KEY = 'agency-kols-table-columns-v1';

/** Fixed-column CSV template for simple import (not a full DB export). */
const KOL_CSV_IMPORT_TEMPLATE = `name,email,phone
Jane Doe,jane@example.com,+60123456789
Ahmad Fariz,ahmad@example.com,+60198765432
Priya Kumar,priya@example.com,+60111222333`;

function downloadCsvFile(content: string, filename: string) {
  const blob = new Blob([content], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

const GENDER_FILTER_OPTIONS: { value: KolGender; label: string }[] = [
  { value: 'FEMALE', label: 'Female' },
  { value: 'MALE', label: 'Male' },
  { value: 'NON_BINARY', label: 'Non-binary' },
  { value: 'OTHER', label: 'Other' },
  { value: 'PREFER_NOT_TO_SAY', label: 'Prefer not to say' },
];

const KOL_TABLE_COLUMN_META: { id: string; label: string }[] = [
  { id: 'name', label: 'Name' },
  { id: 'gender', label: 'Gender' },
  { id: 'instagram', label: 'Instagram' },
  { id: 'location', label: 'Location' },
  { id: 'ethnicity', label: 'Ethnicity' },
  { id: 'tags', label: 'Tags' },
  { id: 'platforms', label: 'Platforms' },
  { id: 'engagement', label: 'Eng. %' },
  { id: 'followers', label: 'Total followers' },
];

const ALL_DATA_COLUMN_IDS = KOL_TABLE_COLUMN_META.map((c) => c.id);

/** Bump when data columns change so migrations can default new ids to visible. */
const KOLS_COLUMNS_SCHEMA_VERSION = 2;

type KolSortOption =
  | 'created-desc'
  | 'created-asc'
  | 'name-asc'
  | 'name-desc'
  | 'email-asc'
  | 'email-desc'
  | 'location-asc'
  | 'gender-asc'
  | 'instagram-asc'
  | 'followers-desc'
  | 'followers-asc'
  | 'engagement-desc'
  | 'engagement-asc'
  | 'linked-asc'
  | 'linked-desc';

type LinkStatusFilter = 'all' | 'linked' | 'not-linked';

const KOL_SORT_OPTIONS: { value: KolSortOption; label: string }[] = [
  { value: 'created-desc', label: 'Newest added' },
  { value: 'created-asc', label: 'Oldest added' },
  { value: 'name-asc', label: 'Name A-Z' },
  { value: 'name-desc', label: 'Name Z-A' },
  { value: 'email-asc', label: 'Email A-Z' },
  { value: 'email-desc', label: 'Email Z-A' },
  { value: 'location-asc', label: 'Location A-Z' },
  { value: 'gender-asc', label: 'Gender A-Z' },
  { value: 'instagram-asc', label: 'Instagram A-Z' },
  { value: 'followers-desc', label: 'Followers high-low' },
  { value: 'followers-asc', label: 'Followers low-high' },
  { value: 'engagement-desc', label: 'Engagement high-low' },
  { value: 'engagement-asc', label: 'Engagement low-high' },
  { value: 'linked-asc', label: 'Account link: not linked first' },
  { value: 'linked-desc', label: 'Account link: linked first' },
];

const LINK_STATUS_FILTER_OPTIONS: { value: LinkStatusFilter; label: string }[] = [
  { value: 'all', label: 'All link statuses' },
  { value: 'not-linked', label: 'Not linked only' },
  { value: 'linked', label: 'Linked only' },
];

function loadVisibleKolColumnIds(): Set<string> {
  try {
    const raw = localStorage.getItem(KOLS_COLUMNS_STORAGE_KEY);
    if (!raw) return new Set(ALL_DATA_COLUMN_IDS);
    const parsed = JSON.parse(raw) as { visible?: unknown; columnSchema?: number };
    const vis = parsed?.visible;
    if (!Array.isArray(vis)) return new Set(ALL_DATA_COLUMN_IDS);
    const set = new Set(
      vis.filter((x): x is string => typeof x === 'string' && ALL_DATA_COLUMN_IDS.includes(x))
    );
    if (set.size === 0) return new Set(ALL_DATA_COLUMN_IDS);
    if (parsed.columnSchema !== KOLS_COLUMNS_SCHEMA_VERSION) {
      for (const id of ALL_DATA_COLUMN_IDS) {
        set.add(id);
      }
    }
    return set;
  } catch {
    return new Set(ALL_DATA_COLUMN_IDS);
  }
}

function kolGenderCellLabel(g: KolGender | null | undefined): string {
  if (!g) return '—';
  return GENDER_FILTER_OPTIONS.find((o) => o.value === g)?.label ?? g;
}

function kolInstagramHandle(row: KOLProfile): string {
  const fromJson = (row.platforms as { instagram?: { handle?: string } } | undefined)?.instagram?.handle;
  const h = row.instagramHandle || fromJson;
  if (!h) return '—';
  return h.startsWith('@') ? h : `@${h}`;
}

function kolIsLinked(row: KOLProfile): boolean {
  return Boolean(row.linkedUser ?? row.userId ?? row.user?.id);
}

function kolAverageEngagement(row: KOLProfile): number | null {
  const platforms = row.platforms as Record<string, { engagementRate?: number }> | undefined;
  if (!platforms) return null;
  let sum = 0;
  let n = 0;
  Object.values(platforms).forEach((p) => {
    if (p?.engagementRate != null && Number.isFinite(p.engagementRate)) {
      sum += p.engagementRate;
      n++;
    }
  });
  return n ? sum / n : null;
}

function formatCompactFollowers(value: number): string {
  return new Intl.NumberFormat('en', {
    notation: 'compact',
    maximumFractionDigits: value >= 1_000_000 ? 1 : 0,
  }).format(value);
}

function MobileFilterChip({ label, onRemove }: { label: string; onRemove: () => void }) {
  return (
    <Badge
      variant="secondary"
      className="min-h-8 shrink-0 gap-1 py-0 pl-2.5 pr-0.5 normal-case tracking-normal"
    >
      <span className="max-w-48 truncate">{label}</span>
      <button
        type="button"
        onClick={onRemove}
        className="inline-flex h-8 w-8 items-center justify-center rounded-full text-glass-text-3 transition-colors hover:bg-glass-4 hover:text-glass-text focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent2/50"
        aria-label={`Remove ${label} filter`}
      >
        <X className="h-3.5 w-3.5" />
      </button>
    </Badge>
  );
}

function compareText(a: string | null | undefined, b: string | null | undefined): number {
  const av = (a || '').trim().toLowerCase();
  const bv = (b || '').trim().toLowerCase();
  if (!av && !bv) return 0;
  if (!av) return 1;
  if (!bv) return -1;
  return av.localeCompare(bv);
}

function compareNumber(a: number | null | undefined, b: number | null | undefined, direction: 'asc' | 'desc'): number {
  const av = a != null && Number.isFinite(a) ? a : null;
  const bv = b != null && Number.isFinite(b) ? b : null;
  if (av == null && bv == null) return 0;
  if (av == null) return 1;
  if (bv == null) return -1;
  return direction === 'asc' ? av - bv : bv - av;
}

function compareKolRows(a: KOLProfile, b: KOLProfile, sortBy: KolSortOption): number {
  const byName = () => compareText(a.name, b.name);
  const byCreated = (direction: 'asc' | 'desc') =>
    compareNumber(
      new Date(a.createdAt).getTime(),
      new Date(b.createdAt).getTime(),
      direction
    ) || byName();

  switch (sortBy) {
    case 'created-asc':
      return byCreated('asc');
    case 'name-asc':
      return compareText(a.name, b.name);
    case 'name-desc':
      return compareText(b.name, a.name);
    case 'email-asc':
      return compareText(a.email, b.email) || byName();
    case 'email-desc':
      return compareText(b.email, a.email) || byName();
    case 'location-asc':
      return compareText(a.locationCity, b.locationCity) || byName();
    case 'gender-asc':
      return compareText(kolGenderCellLabel(a.gender), kolGenderCellLabel(b.gender)) || byName();
    case 'instagram-asc':
      return compareText(kolInstagramHandle(a), kolInstagramHandle(b)) || byName();
    case 'followers-desc':
      return compareNumber(kolTotalFollowers(a), kolTotalFollowers(b), 'desc') || byName();
    case 'followers-asc':
      return compareNumber(kolTotalFollowers(a), kolTotalFollowers(b), 'asc') || byName();
    case 'engagement-desc':
      return compareNumber(kolAverageEngagement(a), kolAverageEngagement(b), 'desc') || byName();
    case 'engagement-asc':
      return compareNumber(kolAverageEngagement(a), kolAverageEngagement(b), 'asc') || byName();
    case 'linked-asc':
      return Number(kolIsLinked(a)) - Number(kolIsLinked(b)) || byName();
    case 'linked-desc':
      return Number(kolIsLinked(b)) - Number(kolIsLinked(a)) || byName();
    case 'created-desc':
    default:
      return byCreated('desc');
  }
}

export default function KOLsPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    ethnicity: '',
    platforms: { instagram: { handle: '', followers: 0 }, tiktok: { handle: '', followers: 0 } },
    notes: '',
    tagIds: [] as string[],
  });
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [showImportModeModal, setShowImportModeModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [importText, setImportText] = useState('');
  const [importing, setImporting] = useState(false);
  const [importResults, setImportResults] = useState<{
    created: number;
    total: number;
    errors: { row: number; error?: string }[];
  } | null>(null);

  const { csvHeaders, csvPreviewRows, columnMapping } = useMemo(() => {
    const matrix = parseCsvLines(importText);
    const headers = matrix[0] ?? [];
    const preview = matrix.length > 1 ? matrix.slice(1, 6) : [];
    return {
      csvHeaders: headers,
      csvPreviewRows: preview,
      columnMapping: resolveCsvMapping(headers),
    };
  }, [importText]);
  const [showSavedSearches, setShowSavedSearches] = useState(false);
  const [searchName, setSearchName] = useState('');
  const [genderFilter, setGenderFilter] = useState<Set<KolGender>>(new Set());
  const [minFollowersFilter, setMinFollowersFilter] = useState('');
  const [maxFollowersFilter, setMaxFollowersFilter] = useState('');
  const [locationFilter, setLocationFilter] = useState('');
  const [linkStatusFilter, setLinkStatusFilter] = useState<LinkStatusFilter>('all');
  const [sortBy, setSortBy] = useState<KolSortOption>('created-desc');
  const [visibleColumnIds, setVisibleColumnIds] = useState<Set<string>>(loadVisibleKolColumnIds);
  const [showColumnPicker, setShowColumnPicker] = useState(false);
  const [showMobileFilters, setShowMobileFilters] = useState(false);

  const meQuery = useQuery({
    queryKey: ['auth', 'me'],
    queryFn: () => api.get('/auth/me').then((r) => r.data),
    staleTime: 5 * 60 * 1000,
  });

  const agencyOrgId = useMemo(() => {
    const agencyMembership = meQuery.data?.memberships?.find(
      (m: { organization?: { type?: string } }) => m.organization?.type === 'AGENCY'
    );
    return agencyMembership?.organizationId ?? '';
  }, [meQuery.data]);

  const tagsFilterKey = useMemo(() => selectedTags.slice().sort().join(','), [selectedTags]);

  // Product analytics (debounced): only whether a search/tag filter is active — never the query text.
  useEffect(() => {
    if (!searchTerm.trim() && selectedTags.length === 0) return;
    const t = setTimeout(() => {
      track('kol_search_performed', {
        has_search: Boolean(searchTerm.trim()),
        filters_count: (searchTerm.trim() ? 1 : 0) + (selectedTags.length > 0 ? 1 : 0),
        tags_count: selectedTags.length,
      });
    }, 800);
    return () => clearTimeout(t);
  }, [searchTerm, tagsFilterKey, selectedTags.length]);

  const kolsQuery = useQuery({
    queryKey: ['agency', 'kols', searchTerm, tagsFilterKey],
    queryFn: () =>
      api
        .get<KOLProfile[]>('/kols', {
          params: { search: searchTerm, tags: selectedTags.join(',') },
        })
        .then((r) => r.data),
    staleTime: 60 * 1000,
  });

  const tagsQuery = useQuery({
    queryKey: ['tags'],
    queryFn: () => api.get<Tag[]>('/tags').then((r) => r.data),
    staleTime: 10 * 60 * 1000,
  });

  const savedSearchesQuery = useQuery({
    queryKey: ['saved-searches', agencyOrgId],
    queryFn: () =>
      api.get('/saved-searches', { params: { organizationId: agencyOrgId } }).then((r) => r.data),
    enabled: Boolean(agencyOrgId),
    staleTime: 60 * 1000,
  });

  const kols = kolsQuery.data ?? [];
  const tags = tagsQuery.data ?? [];
  const savedSearches = savedSearchesQuery.data ?? [];

  useEffect(() => {
    try {
      const visible = ALL_DATA_COLUMN_IDS.filter((id) => visibleColumnIds.has(id));
      localStorage.setItem(
        KOLS_COLUMNS_STORAGE_KEY,
        JSON.stringify({ visible, columnSchema: KOLS_COLUMNS_SCHEMA_VERSION })
      );
    } catch {
      /* ignore quota / private mode */
    }
  }, [visibleColumnIds]);

  const filteredKols = useMemo(() => {
    return kols.filter((k) => {
      if (genderFilter.size > 0) {
        if (!k.gender || !genderFilter.has(k.gender)) return false;
      }
      const total = kolTotalFollowers(k);
      const minN = minFollowersFilter.trim() === '' ? null : Number(minFollowersFilter);
      const maxN = maxFollowersFilter.trim() === '' ? null : Number(maxFollowersFilter);
      if (minN != null && !Number.isNaN(minN) && total < minN) return false;
      if (maxN != null && !Number.isNaN(maxN) && total > maxN) return false;
      if (locationFilter.trim()) {
        const city = (k.locationCity || '').toLowerCase();
        if (!city.includes(locationFilter.trim().toLowerCase())) return false;
      }
      if (linkStatusFilter === 'linked' && !kolIsLinked(k)) return false;
      if (linkStatusFilter === 'not-linked' && kolIsLinked(k)) return false;
      return true;
    });
  }, [kols, genderFilter, minFollowersFilter, maxFollowersFilter, locationFilter, linkStatusFilter]);

  const sortedKols = useMemo(() => {
    return [...filteredKols].sort((a, b) => compareKolRows(a, b, sortBy));
  }, [filteredKols, sortBy]);

  const notLinkedCount = useMemo(() => {
    return kols.filter((k) => !kolIsLinked(k)).length;
  }, [kols]);

  const activeSheetFilterCount =
    (selectedTags.length > 0 ? 1 : 0) +
    (sortBy !== 'created-desc' ? 1 : 0) +
    (linkStatusFilter !== 'all' ? 1 : 0) +
    (genderFilter.size > 0 ? 1 : 0) +
    (minFollowersFilter.trim() !== '' || maxFollowersFilter.trim() !== '' ? 1 : 0) +
    (locationFilter.trim() !== '' ? 1 : 0);

  const toggleGenderFilter = (g: KolGender) => {
    setGenderFilter((prev) => {
      const next = new Set(prev);
      if (next.has(g)) next.delete(g);
      else next.add(g);
      return next;
    });
  };

  const toggleColumnVisibility = (id: string) => {
    setVisibleColumnIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleCreateKOL = async () => {
    try {
      await api.post('/kols', {
        ...formData,
        agencyOrgId,
        ethnicity: formData.ethnicity.trim() || undefined,
      });
      setShowCreateModal(false);
      resetForm();
      await queryClient.invalidateQueries({ queryKey: ['agency', 'kols'] });
    } catch (error) {
      console.error('Failed to create KOL:', error);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      email: '',
      phone: '',
      ethnicity: '',
      platforms: { instagram: { handle: '', followers: 0 }, tiktok: { handle: '', followers: 0 } },
      notes: '',
      tagIds: [],
    });
  };

  const toggleSelectId = (kolId: string) => {
    setSelectedIds((prev) => {
      const n = new Set(prev);
      if (n.has(kolId)) n.delete(kolId);
      else n.add(kolId);
      return n;
    });
  };

  const selectAllOnPage = () => {
    setSelectedIds(new Set(sortedKols.map((k) => k.id)));
  };

  const clearSelection = () => setSelectedIds(new Set());

  const handleExportCsvTemplate = () => {
    downloadCsvFile(KOL_CSV_IMPORT_TEMPLATE, 'kols-import-template.csv');
  };

  const handleBulkDelete = async () => {
    if (selectedIds.size === 0 || !confirm(`Delete ${selectedIds.size} KOL profile(s)?`)) return;
    for (const kid of selectedIds) {
      try {
        await api.delete(`/kols/${kid}`);
      } catch (e) {
        console.error(e);
      }
    }
    clearSelection();
    void queryClient.invalidateQueries({ queryKey: ['agency', 'kols'] });
  };

  const handleFileSelect = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (evt) => {
      setImportText((evt.target?.result as string) || '');
      setImportResults(null);
    };
    reader.readAsText(file);
  };

  const runImport = async () => {
    if (!agencyOrgId || !importText.trim()) return;
    try {
      setImporting(true);
      setImportResults(null);
      const res = await api.post('/kols/bulk-import', {
        agencyOrgId,
        csvText: importText,
      });
      const { created, total, results } = res.data as {
        created: number;
        total: number;
        results?: { row: number; ok: boolean; error?: string }[];
      };
      const errors = (results || []).filter((r) => !r.ok).map((r) => ({ row: r.row, error: r.error }));
      setImportResults({ created, total, errors });
      void queryClient.invalidateQueries({ queryKey: ['agency', 'kols'] });
    } catch (e: any) {
      alert(e.response?.data?.error || 'Import failed');
    } finally {
      setImporting(false);
    }
  };

  const handleSaveSearch = async () => {
    if (!agencyOrgId || !searchName.trim()) return;
    try {
      await api.post('/saved-searches', {
        organizationId: agencyOrgId,
        name: searchName.trim(),
        queryJson: {
          search: searchTerm,
          tags: selectedTags,
          gender: [...genderFilter],
          minFollowers: minFollowersFilter,
          maxFollowers: maxFollowersFilter,
          location: locationFilter,
          linkStatus: linkStatusFilter,
          sortBy,
        },
      });
      setSearchName('');
      await queryClient.invalidateQueries({ queryKey: ['saved-searches', agencyOrgId] });
    } catch (e) {
      console.error(e);
      alert('Failed to save search');
    }
  };

  const applySavedSearch = (item: any) => {
    const q = item.queryJson;
    const parsed = q && typeof q === 'object' ? q : {};
    setSearchTerm(typeof parsed.search === 'string' ? parsed.search : '');
    const t = parsed.tags;
    setSelectedTags(Array.isArray(t) ? t.map(String) : t != null && t !== '' ? [String(t)] : []);
    const g = parsed.gender;
    setGenderFilter(
      new Set(
        Array.isArray(g)
          ? g.filter((x): x is KolGender =>
              GENDER_FILTER_OPTIONS.some((option) => option.value === x)
            )
          : []
      )
    );
    setMinFollowersFilter(typeof parsed.minFollowers === 'string' ? parsed.minFollowers : '');
    setMaxFollowersFilter(typeof parsed.maxFollowers === 'string' ? parsed.maxFollowers : '');
    setLocationFilter(typeof parsed.location === 'string' ? parsed.location : '');
    setLinkStatusFilter(
      LINK_STATUS_FILTER_OPTIONS.some((o) => o.value === parsed.linkStatus)
        ? (parsed.linkStatus as LinkStatusFilter)
        : 'all'
    );
    setSortBy(
      KOL_SORT_OPTIONS.some((o) => o.value === parsed.sortBy)
        ? (parsed.sortBy as KolSortOption)
        : 'created-desc'
    );
    setShowSavedSearches(false);
  };

  const handleDeleteSavedSearch = async (e: React.MouseEvent, id: string) => {
    e.stopPropagation();
    if (!confirm('Remove this saved search?')) return;
    try {
      await api.delete(`/saved-searches/${id}`);
      if (agencyOrgId) await queryClient.invalidateQueries({ queryKey: ['saved-searches', agencyOrgId] });
    } catch (err) {
      console.error(err);
      alert('Failed to delete saved search');
    }
  };

  const tableColumns = useMemo(() => {
    const all: {
      id: string;
      header: string;
      accessor: (row: KOLProfile) => ReactNode;
    }[] = [
      {
        id: 'select',
        header: '',
        accessor: (row: KOLProfile) => (
          <input
            type="checkbox"
            checked={selectedIds.has(row.id)}
            onChange={(e) => {
              e.stopPropagation();
              toggleSelectId(row.id);
            }}
            className="rounded border-glass accent-accent2"
          />
        ),
      },
      {
        id: 'name',
        header: 'Name',
        accessor: (row: KOLProfile) => (
          <div className="flex items-center gap-3">
            {row.profileImageUrl ? (
              <img
                src={resolveMediaUrl(row.profileImageUrl)}
                alt={row.name}
                className="w-10 h-10 rounded-full object-cover ring-1 ring-glass"
              />
            ) : (
              <div className="w-10 h-10 rounded-full bg-accent2/15 flex items-center justify-center">
                <span className="text-accent2 font-semibold">{row.name[0]}</span>
              </div>
            )}
            <div>
              <div className="font-medium text-glass-text flex flex-wrap items-center gap-2">
                {row.name}
                {!kolIsLinked(row) && (
                  <Badge variant="warning" className="text-[10px] font-semibold uppercase tracking-wide">
                    Not linked
                  </Badge>
                )}
              </div>
              <div className="text-sm text-glass-text-3">{row.email}</div>
            </div>
          </div>
        ),
      },
      {
        id: 'gender',
        header: 'Gender',
        accessor: (row: KOLProfile) => (
          <span className="text-sm text-glass-text-2">
            {kolGenderCellLabel(row.gender)}
          </span>
        ),
      },
      {
        id: 'instagram',
        header: 'Instagram',
        accessor: (row: KOLProfile) => (
          <span className="text-sm text-glass-text-2">{kolInstagramHandle(row)}</span>
        ),
      },
      {
        id: 'location',
        header: 'Location',
        accessor: (row: KOLProfile) => (
          <span className="text-sm text-glass-text-2">
            {row.locationCity?.trim() || '—'}
          </span>
        ),
      },
      {
        id: 'ethnicity',
        header: 'Ethnicity',
        accessor: (row: KOLProfile) => (
          <span className="text-sm text-glass-text-2">{row.ethnicity?.trim() || '—'}</span>
        ),
      },
      {
        id: 'tags',
        header: 'Tags',
        accessor: (row: KOLProfile) => (
          <div className="flex flex-wrap gap-1">
            {row.tags?.slice(0, 3).map((t) => (
              <Badge key={t.tag.id} variant="secondary">
                {t.tag.name}
              </Badge>
            ))}
            {(row.tags?.length || 0) > 3 && (
              <Badge variant="outline">+{(row.tags?.length || 0) - 3}</Badge>
            )}
          </div>
        ),
      },
      {
        id: 'platforms',
        header: 'Platforms',
        accessor: (row: KOLProfile) => {
          const platforms = row.platforms as Record<string, { handle?: string }> | undefined;
          const platformNames = platforms
            ? Object.keys(platforms).filter((p) => platforms[p]?.handle)
            : [];
          return (
            <div className="text-sm text-glass-text-2">
              {platformNames.length > 0 ? platformNames.join(', ') : 'None'}
            </div>
          );
        },
      },
      {
        id: 'engagement',
        header: 'Eng. %',
        accessor: (row: KOLProfile) => {
          const average = kolAverageEngagement(row);
          return average != null ? `${average.toFixed(2)}%` : '—';
        },
      },
      {
        id: 'followers',
        header: 'Total Followers',
        accessor: (row: KOLProfile) => kolTotalFollowers(row).toLocaleString(),
      },
    ];

    return all
      .filter((c) => c.id === 'select' || visibleColumnIds.has(c.id))
      .map(({ header, accessor }) => ({ header, accessor }));
  }, [visibleColumnIds, selectedIds]);

  return (
    <div className="flex flex-col gap-6">
      {/* Desktop header */}
      <div className="order-1 hidden items-center justify-between md:flex">
        <div>
          <h1 className="page-title">KOL Database</h1>
          <p className="text-glass-text-2 text-sm mt-1">Manage your influencer profiles</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" onClick={() => navigate('/agency/kols/table')} disabled={!agencyOrgId}>
            <Table2 className="w-4 h-4 mr-2" />
            Full table
          </Button>
          <Button variant="outline" onClick={() => setShowImportModeModal(true)} disabled={!agencyOrgId}>
            <FileSpreadsheet className="w-4 h-4 mr-2" />
            Excel import
          </Button>
          <Button data-help-id="kol.create_button" onClick={() => setShowCreateModal(true)}>
            <Plus className="w-4 h-4 mr-2" />
            Add KOL
          </Button>
        </div>
      </div>

      {/* Mobile header */}
      <div className="order-1 flex items-center justify-between gap-3 md:hidden">
        <div className="min-w-0">
          <h1 className="page-title text-[1.35rem]">KOL Database</h1>
          <p className="mt-0.5 text-xs text-glass-text-3">
            {sortedKols.length.toLocaleString()} creators
          </p>
        </div>
        <Button
          data-help-id="kol.create_button"
          size="icon"
          className="h-11 w-11 shrink-0"
          onClick={() => setShowCreateModal(true)}
          aria-label="Add KOL"
          title="Add KOL"
        >
          <Plus className="h-5 w-5" />
        </Button>
      </div>

      <AIKOLDiscoveryPanel
        mode="agency"
        title="AI Talent Scout"
        kols={kols.slice(0, 50).map((k) => ({ id: k.id, name: k.name }))}
        className="order-6 md:order-2"
      />

      {selectedIds.size > 0 && (
        <div className="order-4 glass-card flex items-center gap-3 p-3 text-sm border-amber-400/30 bg-amber-400/10 md:order-3">
          <span className="font-medium text-amber-500 dark:text-amber-300">
            {selectedIds.size} selected
          </span>
          <Button size="sm" variant="outline" onClick={selectAllOnPage}>
            Select all on page
          </Button>
          <Button size="sm" variant="outline" onClick={clearSelection}>
            Clear
          </Button>
          <Button
            size="sm"
            variant="outline"
            className="text-rose-500 dark:text-rose-300"
            onClick={handleBulkDelete}
          >
            <Trash2 className="w-4 h-4 mr-1" />
            Delete selected
          </Button>
        </div>
      )}

      <section className="order-2 space-y-3 md:hidden" aria-label="Search and filters">
        <div className="flex gap-2">
          <div className="relative min-w-0 flex-1">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-glass-text-3" />
            <Input
              type="search"
              placeholder="Search name or email"
              value={searchTerm}
              onChange={(event) => setSearchTerm(event.target.value)}
              className="h-11 pl-10 pr-3"
              aria-label="Search KOLs by name or email"
            />
          </div>
          <Button
            type="button"
            variant="outline"
            size="icon"
            className="relative h-11 w-11 shrink-0"
            onClick={() => setShowMobileFilters(true)}
            aria-label={
              activeSheetFilterCount > 0
                ? `Open filters, ${activeSheetFilterCount} active`
                : 'Open filters'
            }
          >
            <Filter className="h-5 w-5" />
            {activeSheetFilterCount > 0 && (
              <span className="absolute -right-1 -top-1 flex h-5 min-w-5 items-center justify-center rounded-full bg-accent2 px-1 text-[10px] font-bold text-white dark:bg-violet-400 dark:text-slate-950">
                {activeSheetFilterCount}
              </span>
            )}
          </Button>
        </div>

        {(searchTerm || activeSheetFilterCount > 0) && (
          <div className="-mx-1 flex gap-2 overflow-x-auto px-1 pb-1" aria-label="Active filters">
            {searchTerm && (
              <MobileFilterChip label={`Search: ${searchTerm}`} onRemove={() => setSearchTerm('')} />
            )}
            {selectedTags.map((tagId) => {
              const tag = tags.find((item) => item.id === tagId);
              return (
                <MobileFilterChip
                  key={tagId}
                  label={`Tag: ${tag?.name || 'Unknown'}`}
                  onRemove={() => setSelectedTags((current) => current.filter((id) => id !== tagId))}
                />
              );
            })}
            {sortBy !== 'created-desc' && (
              <MobileFilterChip
                label={KOL_SORT_OPTIONS.find((option) => option.value === sortBy)?.label || 'Sort'}
                onRemove={() => setSortBy('created-desc')}
              />
            )}
            {linkStatusFilter !== 'all' && (
              <MobileFilterChip
                label={
                  LINK_STATUS_FILTER_OPTIONS.find((option) => option.value === linkStatusFilter)?.label ||
                  'Account link'
                }
                onRemove={() => setLinkStatusFilter('all')}
              />
            )}
            {genderFilter.size > 0 && (
              <MobileFilterChip
                label={`Gender: ${[...genderFilter]
                  .map((gender) => GENDER_FILTER_OPTIONS.find((option) => option.value === gender)?.label || gender)
                  .join(', ')}`}
                onRemove={() => setGenderFilter(new Set())}
              />
            )}
            {(minFollowersFilter.trim() !== '' || maxFollowersFilter.trim() !== '') && (
              <MobileFilterChip
                label={`Followers: ${minFollowersFilter.trim() || '0'}–${maxFollowersFilter.trim() || '∞'}`}
                onRemove={() => {
                  setMinFollowersFilter('');
                  setMaxFollowersFilter('');
                }}
              />
            )}
            {locationFilter.trim() !== '' && (
              <MobileFilterChip
                label={`Location: ${locationFilter.trim()}`}
                onRemove={() => setLocationFilter('')}
              />
            )}
          </div>
        )}
      </section>

      {/* Filters */}
      <div className="order-4 hidden glass-card p-4 space-y-4 md:block">
        <div className="flex items-end gap-4 flex-wrap">
          <div className="flex-1 min-w-[200px]">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-glass-text-3 w-5 h-5" />
              <Input
                placeholder="Search by name or email..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
          </div>
          <div className="w-64 min-w-[200px]">
            <Select
              value={selectedTags[0] || ''}
              onChange={(e) => setSelectedTags(e.target.value ? [e.target.value] : [])}
            >
              <option value="">All Tags</option>
              {tags.map((tag) => (
                <option key={tag.id} value={tag.id}>{tag.name}</option>
              ))}
            </Select>
          </div>
          <div className="w-56 min-w-[180px]">
            <label className="block text-xs text-glass-text-3 mb-1">Sort by</label>
            <Select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value as KolSortOption)}
            >
              {KOL_SORT_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </Select>
          </div>
          <div className="w-48 min-w-[170px]">
            <label className="block text-xs text-glass-text-3 mb-1">Account link</label>
            <Select
              value={linkStatusFilter}
              onChange={(e) => setLinkStatusFilter(e.target.value as LinkStatusFilter)}
            >
              {LINK_STATUS_FILTER_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </Select>
            {notLinkedCount > 0 && (
              <div className="mt-1 text-xs text-glass-text-3">
                {notLinkedCount.toLocaleString()} not linked
              </div>
            )}
          </div>
          <div className="min-w-[220px] max-w-md">
            <label className="block text-xs text-glass-text-3 mb-1">Gender</label>
            <div className="flex flex-wrap gap-2">
              {GENDER_FILTER_OPTIONS.map(({ value, label }) => (
                <label
                  key={value}
                  className="inline-flex items-center gap-1.5 text-sm text-glass-text-2 cursor-pointer"
                >
                  <input
                    type="checkbox"
                    className="rounded border-glass accent-accent2"
                    checked={genderFilter.has(value)}
                    onChange={() => toggleGenderFilter(value)}
                  />
                  {label}
                </label>
              ))}
            </div>
          </div>
          <div className="w-28 min-w-[100px]">
            <label className="block text-xs text-glass-text-3 mb-1">Min followers</label>
            <Input
              type="number"
              min={0}
              placeholder="0"
              value={minFollowersFilter}
              onChange={(e) => setMinFollowersFilter(e.target.value)}
            />
          </div>
          <div className="w-28 min-w-[100px]">
            <label className="block text-xs text-glass-text-3 mb-1">Max followers</label>
            <Input
              type="number"
              min={0}
              placeholder="∞"
              value={maxFollowersFilter}
              onChange={(e) => setMaxFollowersFilter(e.target.value)}
            />
          </div>
          <div className="w-44 min-w-[160px]">
            <label className="block text-xs text-glass-text-3 mb-1">Location</label>
            <Input
              placeholder="City contains…"
              value={locationFilter}
              onChange={(e) => setLocationFilter(e.target.value)}
            />
          </div>
          <div className="w-44 min-w-[140px]">
            <label className="block text-xs text-glass-text-3 mb-1">Save as</label>
            <Input
              placeholder="Search name"
              value={searchName}
              onChange={(e) => setSearchName(e.target.value)}
              disabled={!agencyOrgId}
            />
          </div>
          <Button
            type="button"
            variant="outline"
            disabled={!agencyOrgId || !searchName.trim()}
            onClick={handleSaveSearch}
            className="shrink-0"
          >
            <Bookmark className="w-4 h-4 mr-2" />
            Save search
          </Button>
          <div className="relative shrink-0">
            <Button
              type="button"
              variant="outline"
              disabled={!agencyOrgId}
              onClick={() => setShowSavedSearches((v) => !v)}
            >
              {showSavedSearches ? (
                <BookmarkCheck className="w-4 h-4 mr-2" />
              ) : (
                <Bookmark className="w-4 h-4 mr-2" />
              )}
              Saved searches
            </Button>
            {showSavedSearches && agencyOrgId && (
              <div className="absolute right-0 top-full mt-1 w-72 glass-card backdrop-blur-3xl shadow-2xl z-20 py-1 max-h-64 overflow-y-auto">
                {savedSearches.length === 0 ? (
                  <div className="px-3 py-2 text-sm text-glass-text-3">
                    No saved searches yet.
                  </div>
                ) : (
                  savedSearches.map((s: { id: string; name: string; queryJson?: unknown }) => (
                    <div
                      key={s.id}
                      className="flex items-center justify-between gap-2 px-3 py-2 hover:bg-glass-3 border-b border-glass last:border-0"
                    >
                      <button
                        type="button"
                        className="text-left text-sm font-medium text-glass-text flex-1 min-w-0 truncate"
                        onClick={() => applySavedSearch(s)}
                      >
                        {s.name}
                      </button>
                      <button
                        type="button"
                        className="p-1 rounded text-glass-text-3 hover:text-rose-500 dark:hover:text-rose-300 hover:bg-rose-500/10 shrink-0"
                        title="Delete saved search"
                        onClick={(e) => handleDeleteSavedSearch(e, s.id)}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  ))
                )}
              </div>
            )}
          </div>
          <Button
            type="button"
            variant="outline"
            className="shrink-0"
            onClick={() => setShowColumnPicker(true)}
          >
            <Columns2 className="w-4 h-4 mr-2" />
            Columns
          </Button>
        </div>

        {/* Selected Filters */}
        {(searchTerm ||
          selectedTags.length > 0 ||
          genderFilter.size > 0 ||
          minFollowersFilter.trim() !== '' ||
          maxFollowersFilter.trim() !== '' ||
          locationFilter.trim() !== '' ||
          linkStatusFilter !== 'all' ||
          sortBy !== 'created-desc') && (
          <div className="flex flex-wrap items-center gap-2">
            <Filter className="w-4 h-4 text-glass-text-3 shrink-0" />
            <span className="text-sm text-glass-text-2">Active filters:</span>
            {searchTerm && (
              <Badge variant="secondary">
                Search: {searchTerm}
                <button type="button" onClick={() => setSearchTerm('')} className="ml-2">
                  ×
                </button>
              </Badge>
            )}
            {selectedTags.map((tagId) => {
              const tag = tags.find((t) => t.id === tagId);
              return (
                <Badge key={tagId} variant="secondary">
                  {tag?.name}
                  <button type="button" onClick={() => setSelectedTags([])} className="ml-2">
                    ×
                  </button>
                </Badge>
              );
            })}
            {sortBy !== 'created-desc' && (
              <Badge variant="secondary">
                Sort:{' '}
                {KOL_SORT_OPTIONS.find((o) => o.value === sortBy)?.label ?? sortBy}
                <button type="button" onClick={() => setSortBy('created-desc')} className="ml-2">
                  ×
                </button>
              </Badge>
            )}
            {linkStatusFilter !== 'all' && (
              <Badge variant="secondary">
                {LINK_STATUS_FILTER_OPTIONS.find((o) => o.value === linkStatusFilter)?.label ?? linkStatusFilter}
                <button type="button" onClick={() => setLinkStatusFilter('all')} className="ml-2">
                  ×
                </button>
              </Badge>
            )}
            {genderFilter.size > 0 && (
              <Badge variant="secondary">
                Gender:{' '}
                {[...genderFilter]
                  .map((g) => GENDER_FILTER_OPTIONS.find((o) => o.value === g)?.label ?? g)
                  .join(', ')}
                <button
                  type="button"
                  onClick={() => setGenderFilter(new Set())}
                  className="ml-2"
                >
                  ×
                </button>
              </Badge>
            )}
            {(minFollowersFilter.trim() !== '' || maxFollowersFilter.trim() !== '') && (
              <Badge variant="secondary">
                Followers: {minFollowersFilter.trim() || '0'} – {maxFollowersFilter.trim() || '∞'}
                <button
                  type="button"
                  onClick={() => {
                    setMinFollowersFilter('');
                    setMaxFollowersFilter('');
                  }}
                  className="ml-2"
                >
                  ×
                </button>
              </Badge>
            )}
            {locationFilter.trim() !== '' && (
              <Badge variant="secondary">
                Location: {locationFilter.trim()}
                <button type="button" onClick={() => setLocationFilter('')} className="ml-2">
                  ×
                </button>
              </Badge>
            )}
          </div>
        )}
      </div>

      {/* Mobile row list */}
      <div className="order-3 overflow-hidden glass-card md:hidden">
        {kolsQuery.isPending ? (
          <div className="divide-y divide-glass" aria-label="Loading KOLs">
            {Array.from({ length: 7 }).map((_, index) => (
              <div key={index} className="flex min-h-[60px] animate-pulse items-center gap-3 px-3 py-2">
                <div className="h-9 w-9 shrink-0 rounded-full bg-glass-3" />
                <div className="min-w-0 flex-1 space-y-2">
                  <div className="h-3 w-32 rounded bg-glass-3" />
                  <div className="h-2.5 w-24 rounded bg-glass-3" />
                </div>
                <div className="h-4 w-12 rounded bg-glass-3" />
              </div>
            ))}
          </div>
        ) : sortedKols.length === 0 ? (
          <div className="px-5 py-12 text-center">
            <p className="text-sm font-medium text-glass-text">No KOLs found</p>
            <p className="mt-1 text-xs text-glass-text-3">
              Adjust your filters or add your first influencer.
            </p>
          </div>
        ) : (
          <div className="divide-y divide-glass">
            {sortedKols.map((row) => {
              const engagement = kolAverageEngagement(row);
              const handle = kolInstagramHandle(row);
              const meta = [handle !== '—' ? handle : null, row.locationCity?.trim() || null]
                .filter(Boolean)
                .join(' · ');
              return (
                <button
                  key={row.id}
                  type="button"
                  onClick={() => navigate(`/agency/kols/${kolUrlSegment(row)}`)}
                  className="flex min-h-[60px] w-full cursor-pointer items-center gap-3 px-3 py-2 text-left transition-colors hover:bg-glass-3 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-accent2/50"
                  aria-label={`Open ${row.name}`}
                >
                  {row.profileImageUrl ? (
                    <img
                      src={resolveMediaUrl(row.profileImageUrl)}
                      alt=""
                      className="h-9 w-9 shrink-0 rounded-full object-cover ring-1 ring-glass"
                    />
                  ) : (
                    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-accent2/15 text-sm font-semibold text-accentText">
                      {row.name?.[0]?.toUpperCase() || '?'}
                    </span>
                  )}
                  <span className="min-w-0 flex-1">
                    <span className="flex min-w-0 items-center gap-1.5">
                      <span className="truncate text-sm font-semibold text-glass-text">{row.name}</span>
                      {!kolIsLinked(row) && (
                        <Badge
                          variant="warning"
                          className="shrink-0 px-1.5 py-0 text-[8.5px] leading-4 tracking-[0.03em]"
                        >
                          Not linked
                        </Badge>
                      )}
                    </span>
                    <span className="mt-0.5 block truncate text-xs text-glass-text-3">
                      {meta || row.email || 'No handle or location'}
                    </span>
                  </span>
                  <span className="shrink-0 text-right">
                    <span className="stat-display block text-sm text-glass-text">
                      {formatCompactFollowers(kolTotalFollowers(row))}
                    </span>
                    <span className="mt-1 block text-[11px] text-glass-text-3">
                      {engagement == null ? '—' : `${engagement.toFixed(1)}%`}
                    </span>
                  </span>
                </button>
              );
            })}
          </div>
        )}
      </div>

      {/* Desktop table */}
      <div className="order-5 hidden glass-card overflow-hidden md:block">
        <DataTable
          data={sortedKols}
          columns={tableColumns}
          onRowClick={(kol) => navigate(`/agency/kols/${kolUrlSegment(kol)}`)}
          isLoading={kolsQuery.isPending}
          emptyMessage="No KOLs found. Add your first influencer to get started!"
        />
      </div>

      <Modal
        isOpen={showMobileFilters}
        onClose={() => setShowMobileFilters(false)}
        title="Filter KOLs"
        size="lg"
        mobilePresentation="sheet"
        footer={
          <Button type="button" className="h-11 w-full" onClick={() => setShowMobileFilters(false)}>
            Show {sortedKols.length.toLocaleString()} KOLs
          </Button>
        }
      >
        <div className="space-y-5">
              <p className="text-xs text-glass-text-3" aria-live="polite">
                {sortedKols.length.toLocaleString()} matching creators
              </p>
              <div>
                <label htmlFor="mobile-kol-tag" className="mb-1.5 block text-xs font-medium text-glass-text-2">
                  Tag
                </label>
                <Select
                  id="mobile-kol-tag"
                  value={selectedTags[0] || ''}
                  onChange={(event) => setSelectedTags(event.target.value ? [event.target.value] : [])}
                  className="h-11"
                >
                  <option value="">All tags</option>
                  {tags.map((tag) => (
                    <option key={tag.id} value={tag.id}>
                      {tag.name}
                    </option>
                  ))}
                </Select>
              </div>

              <div>
                <label htmlFor="mobile-kol-sort" className="mb-1.5 block text-xs font-medium text-glass-text-2">
                  Sort by
                </label>
                <Select
                  id="mobile-kol-sort"
                  value={sortBy}
                  onChange={(event) => setSortBy(event.target.value as KolSortOption)}
                  className="h-11"
                >
                  {KOL_SORT_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </Select>
              </div>

              <fieldset>
                <legend className="mb-1.5 text-xs font-medium text-glass-text-2">Account link</legend>
                <div className="grid grid-cols-3 gap-1 rounded-xl border border-glass bg-glass-2 p-1">
                  {([
                    { value: 'all', label: 'All' },
                    { value: 'not-linked', label: 'Not linked' },
                    { value: 'linked', label: 'Linked' },
                  ] as const).map((option) => {
                    const active = linkStatusFilter === option.value;
                    return (
                      <button
                        key={option.value}
                        type="button"
                        onClick={() => setLinkStatusFilter(option.value)}
                        className={`min-h-11 rounded-lg px-2 text-xs font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent2/50 ${
                          active
                            ? 'bg-accent2 text-white shadow-sm dark:bg-violet-400 dark:text-slate-950'
                            : 'text-glass-text-2 hover:bg-glass-3 hover:text-glass-text'
                        }`}
                        aria-pressed={active}
                      >
                        {option.label}
                      </button>
                    );
                  })}
                </div>
                {notLinkedCount > 0 && (
                  <p className="mt-1.5 text-xs text-glass-text-3">
                    {notLinkedCount.toLocaleString()} profiles are not linked
                  </p>
                )}
              </fieldset>

              <fieldset>
                <legend className="mb-1.5 text-xs font-medium text-glass-text-2">Gender</legend>
                <div className="flex flex-wrap gap-2">
                  {GENDER_FILTER_OPTIONS.map(({ value, label }) => {
                    const active = genderFilter.has(value);
                    return (
                      <button
                        key={value}
                        type="button"
                        onClick={() => toggleGenderFilter(value)}
                        className={`min-h-11 rounded-full border px-3 text-xs font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent2/50 ${
                          active
                            ? 'border-accent2/40 bg-accent2/15 text-accentText'
                            : 'border-glass bg-glass-2 text-glass-text-2 hover:bg-glass-3 hover:text-glass-text'
                        }`}
                        aria-pressed={active}
                      >
                        {label}
                      </button>
                    );
                  })}
                </div>
              </fieldset>

              <div>
                <span className="mb-1.5 block text-xs font-medium text-glass-text-2">Follower range</span>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label htmlFor="mobile-min-followers" className="sr-only">Minimum followers</label>
                    <Input
                      id="mobile-min-followers"
                      type="number"
                      min={0}
                      placeholder="Minimum"
                      value={minFollowersFilter}
                      onChange={(event) => setMinFollowersFilter(event.target.value)}
                      className="h-11"
                    />
                  </div>
                  <div>
                    <label htmlFor="mobile-max-followers" className="sr-only">Maximum followers</label>
                    <Input
                      id="mobile-max-followers"
                      type="number"
                      min={0}
                      placeholder="Maximum"
                      value={maxFollowersFilter}
                      onChange={(event) => setMaxFollowersFilter(event.target.value)}
                      className="h-11"
                    />
                  </div>
                </div>
              </div>

              <div>
                <label htmlFor="mobile-kol-location" className="mb-1.5 block text-xs font-medium text-glass-text-2">
                  Location
                </label>
                <Input
                  id="mobile-kol-location"
                  placeholder="City contains…"
                  value={locationFilter}
                  onChange={(event) => setLocationFilter(event.target.value)}
                  className="h-11"
                />
              </div>

              <div className="space-y-2 rounded-2xl border border-glass bg-glass-2 p-3">
                <label htmlFor="mobile-search-name" className="block text-xs font-medium text-glass-text-2">
                  Save this search
                </label>
                <div className="flex gap-2">
                  <Input
                    id="mobile-search-name"
                    placeholder="Search name"
                    value={searchName}
                    onChange={(event) => setSearchName(event.target.value)}
                    disabled={!agencyOrgId}
                    className="h-11 min-w-0 flex-1"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    size="icon"
                    className="h-11 w-11 shrink-0"
                    disabled={!agencyOrgId || !searchName.trim()}
                    onClick={handleSaveSearch}
                    aria-label="Save search"
                    title="Save search"
                  >
                    <Bookmark className="h-4 w-4" />
                  </Button>
                </div>
              </div>

              <div>
                <div className="mb-1.5 flex items-center justify-between gap-2">
                  <h3 className="text-xs font-medium text-glass-text-2">Saved searches</h3>
                  <span className="text-[11px] text-glass-text-3">{savedSearches.length}</span>
                </div>
                <div className="overflow-hidden rounded-xl border border-glass bg-glass-2">
                  {savedSearches.length === 0 ? (
                    <p className="px-3 py-4 text-xs text-glass-text-3">No saved searches yet.</p>
                  ) : (
                    savedSearches.map((saved: { id: string; name: string; queryJson?: unknown }) => (
                      <div
                        key={saved.id}
                        className="flex min-h-11 items-center border-b border-glass pl-3 last:border-b-0"
                      >
                        <button
                          type="button"
                          className="min-h-11 min-w-0 flex-1 truncate text-left text-sm font-medium text-glass-text focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-accent2/50"
                          onClick={() => applySavedSearch(saved)}
                        >
                          {saved.name}
                        </button>
                        <button
                          type="button"
                          className="inline-flex h-11 w-11 shrink-0 items-center justify-center text-glass-text-3 transition-colors hover:bg-rose-500/10 hover:text-rose-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-rose-500/50 dark:hover:text-rose-300"
                          onClick={(event) => handleDeleteSavedSearch(event, saved.id)}
                          aria-label={`Delete saved search ${saved.name}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    ))
                  )}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-2">
                <Button
                  type="button"
                  variant="outline"
                  className="h-11 px-3"
                  onClick={() => {
                    setShowMobileFilters(false);
                    setShowColumnPicker(true);
                  }}
                >
                  <Columns2 className="mr-2 h-4 w-4" />
                  Columns
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  className="h-11 px-3"
                  disabled={!agencyOrgId}
                  onClick={() => {
                    setShowMobileFilters(false);
                    navigate('/agency/kols/table');
                  }}
                >
                  <Table2 className="mr-2 h-4 w-4" />
                  Full table
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  className="col-span-2 h-11"
                  disabled={!agencyOrgId}
                  onClick={() => {
                    setShowMobileFilters(false);
                    setShowImportModeModal(true);
                  }}
                >
                  <FileSpreadsheet className="mr-2 h-4 w-4" />
                  Excel import
                </Button>
              </div>
        </div>
      </Modal>

      {/* Create Modal */}
      <Modal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        title="Add New KOL"
        size="lg"
        footer={
          <div className="flex justify-end gap-3">
            <Button variant="outline" onClick={() => setShowCreateModal(false)}>
              Cancel
            </Button>
            <Button onClick={handleCreateKOL}>Create KOL</Button>
          </div>
        }
      >
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-glass-text mb-1">Name *</label>
            <Input
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder="Influencer name"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-glass-text mb-1">Email</label>
              <Input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                placeholder="email@example.com"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-glass-text mb-1">Phone</label>
              <Input
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                placeholder="+60 12-345 6789"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-glass-text mb-1">Ethnicity (optional)</label>
            <Input
              value={formData.ethnicity}
              onChange={(e) => setFormData({ ...formData, ethnicity: e.target.value })}
              placeholder="e.g. Chinese, Malay, Indian"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-glass-text mb-1">
              Instagram Handle
            </label>
            <Input
              value={formData.platforms.instagram.handle}
              onChange={(e) => setFormData({
                ...formData,
                platforms: {
                  ...formData.platforms,
                  instagram: { ...formData.platforms.instagram, handle: e.target.value }
                }
              })}
              placeholder="@username"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-glass-text mb-1">
              Instagram Followers
            </label>
            <Input
              type="number"
              value={formData.platforms.instagram.followers}
              onChange={(e) => setFormData({
                ...formData,
                platforms: {
                  ...formData.platforms,
                  instagram: { ...formData.platforms.instagram, followers: parseInt(e.target.value) || 0 }
                }
              })}
              placeholder="10000"
            />
          </div>

          <TagSelector
            tags={tags}
            selectedTagIds={formData.tagIds}
            onChange={(selectedIds) => setFormData({ ...formData, tagIds: selectedIds })}
            label="Tags"
          />

          <div>
            <label className="block text-sm font-medium text-glass-text mb-1">Notes</label>
            <textarea
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              className="glass-input w-full px-3 py-2 text-sm"
              rows={3}
              placeholder="Additional notes about this influencer..."
            />
          </div>
        </div>
      </Modal>

      <Modal
        isOpen={showColumnPicker}
        onClose={() => setShowColumnPicker(false)}
        title="Table columns"
        size="md"
        footer={
          <div className="flex justify-end">
            <Button onClick={() => setShowColumnPicker(false)}>Done</Button>
          </div>
        }
      >
        <p className="text-sm text-glass-text-2 mb-4">
          Choose which columns appear in the KOL table. Your choices are saved in this browser.
        </p>
        <ul className="space-y-2">
          {KOL_TABLE_COLUMN_META.map(({ id, label }) => (
            <li key={id}>
              <label className="flex items-center gap-2 text-sm text-glass-text cursor-pointer">
                <input
                  type="checkbox"
                  className="rounded border-glass accent-accent2"
                  checked={visibleColumnIds.has(id)}
                  onChange={() => toggleColumnVisibility(id)}
                />
                {label}
              </label>
            </li>
          ))}
        </ul>
      </Modal>

      <Modal
        isOpen={showImportModeModal}
        onClose={() => setShowImportModeModal(false)}
        title="Choose import mode"
        size="lg"
      >
        <p className="text-sm text-glass-text-2 mb-4">
          Pick the format that matches your spreadsheet. Both options add or update KOL profiles in your
          agency database.
        </p>
        <div className="grid gap-3 sm:grid-cols-2">
          <button
            type="button"
            className="group text-left rounded-xl border border-glass bg-glass-2 p-4 hover:border-accent2/50 hover:bg-glass-3 transition-colors"
            onClick={() => {
              setShowImportModeModal(false);
              navigate('/agency/kols/bulk-import');
            }}
          >
            <div className="flex items-start justify-between gap-2 mb-2">
              <div className="flex items-center gap-2">
                <FileSpreadsheet className="w-5 h-5 text-accent2" />
                <span className="font-semibold text-glass-text">Excel import</span>
              </div>
              <ArrowRight className="w-4 h-4 text-glass-text-3 group-hover:text-accent2 transition-colors" />
            </div>
            <p className="text-sm text-glass-text-2">
              For complex spreadsheets with many columns, custom headers, or data that does not match a
              fixed template. Map columns step by step before importing.
            </p>
          </button>

          <button
            type="button"
            className="group text-left rounded-xl border border-glass bg-glass-2 p-4 hover:border-accent2/50 hover:bg-glass-3 transition-colors"
            onClick={() => {
              setShowImportModeModal(false);
              setShowImportModal(true);
            }}
          >
            <div className="flex items-start justify-between gap-2 mb-2">
              <div className="flex items-center gap-2">
                <Upload className="w-5 h-5 text-accent2" />
                <span className="font-semibold text-glass-text">CSV import &amp; export</span>
              </div>
              <ArrowRight className="w-4 h-4 text-glass-text-3 group-hover:text-accent2 transition-colors" />
            </div>
            <p className="text-sm text-glass-text-2">
              For simpler lists with standard columns (name, email, phone). Import a CSV or export your
              current KOLs to fill in the template and re-import.
            </p>
          </button>
        </div>
      </Modal>

      <Modal
        isOpen={showImportModal}
        onClose={() => {
          setShowImportModal(false);
          setImportResults(null);
        }}
        title="CSV import & export"
        size="lg"
        footer={
          <div className="flex flex-wrap items-center justify-between gap-3">
            <Button variant="outline" onClick={handleExportCsvTemplate}>
              <Download className="w-4 h-4 mr-2" />
              Export CSV template
            </Button>
            <div className="flex gap-3">
            <Button
              variant="outline"
              onClick={() => {
                setShowImportModal(false);
                setImportResults(null);
              }}
            >
              Cancel
            </Button>
            {importResults ? (
              <>
                <Button
                  variant="outline"
                  onClick={() => {
                    setImportResults(null);
                  }}
                >
                  Import more
                </Button>
                <Button
                  onClick={() => {
                    setShowImportModal(false);
                    setImportText('');
                    setImportResults(null);
                  }}
                >
                  Done
                </Button>
              </>
            ) : (
              <Button onClick={runImport} disabled={importing || !importText.trim()}>
                {importing ? 'Importing...' : 'Import'}
              </Button>
            )}
            </div>
          </div>
        }
      >
        <div className="space-y-4">
          <p className="text-sm text-glass-text-2">
            Use this mode for straightforward spreadsheets with fixed columns.{' '}
            <strong className="text-glass-text">Export CSV template</strong> downloads a blank template
            with column headers and sample rows — not your full database. Upload a{' '}
            <code className="text-xs bg-glass-3 text-glass-text px-1 rounded">.csv</code>{' '}
            file or paste below. Header row required. Columns are matched to{' '}
            <strong className="text-glass-text">name</strong>,{' '}
            <strong className="text-glass-text">email</strong>, and{' '}
            <strong className="text-glass-text">phone</strong> (case-insensitive;{' '}
            <code className="text-xs bg-glass-3 text-glass-text px-1">display_name</code>{' '}
            maps to name).
          </p>

          <div>
            <label className="block text-sm font-medium text-glass-text mb-1">
              CSV file
            </label>
            <input
              type="file"
              accept=".csv,text/csv"
              onChange={handleFileSelect}
              className="block w-full text-sm text-glass-text-2 file:mr-3 file:py-2 file:px-3 file:rounded-md file:border file:border-glass file:bg-glass-2 file:text-glass-text file:text-sm hover:file:bg-glass-3"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-glass-text mb-1">
              Or paste CSV text directly
            </label>
            <textarea
              className="glass-input w-full p-3 font-mono text-sm min-h-[120px]"
              value={importText}
              onChange={(e) => {
                setImportText(e.target.value);
                setImportResults(null);
              }}
              placeholder={'name,email,phone\nJane Doe,jane@example.com,+60123456789'}
            />
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="mt-2"
              onClick={() => {
                setImportText('name,email,phone\nSample KOL,sample@example.com,+60000000000');
                setImportResults(null);
              }}
            >
              Load sample
            </Button>
          </div>

          {csvHeaders.length > 0 && (
            <div className="rounded-xl border border-glass bg-glass-2 p-3 text-sm">
              <div className="font-medium text-glass-text mb-2">
                Column mapping (preview)
              </div>
              <ul className="space-y-1 text-glass-text-2">
                <li>
                  <span className="font-mono text-xs text-accent2">name</span>
                  <span className="text-glass-text-3"> ← </span>
                  {columnMapping.name ? (
                    <span className="font-medium text-glass-text">
                      {columnMapping.name}
                    </span>
                  ) : (
                    <span className="text-amber-500 dark:text-amber-300">
                      no matching column
                    </span>
                  )}
                </li>
                <li>
                  <span className="font-mono text-xs text-accent2">email</span>
                  <span className="text-glass-text-3"> ← </span>
                  {columnMapping.email ? (
                    <span className="font-medium text-glass-text">
                      {columnMapping.email}
                    </span>
                  ) : (
                    <span className="text-glass-text-3">optional — no column</span>
                  )}
                </li>
                <li>
                  <span className="font-mono text-xs text-accent2">phone</span>
                  <span className="text-glass-text-3"> ← </span>
                  {columnMapping.phone ? (
                    <span className="font-medium text-glass-text">
                      {columnMapping.phone}
                    </span>
                  ) : (
                    <span className="text-glass-text-3">optional — no column</span>
                  )}
                </li>
              </ul>
            </div>
          )}

          {csvHeaders.length > 0 && (
            <div>
              <div className="text-sm font-medium text-glass-text mb-2">
                First rows (preview, up to 5)
              </div>
              <div className="overflow-x-auto rounded-xl border border-glass">
                <table className="min-w-full text-sm">
                  <thead className="bg-glass-3">
                    <tr>
                      {csvHeaders.map((h, i) => (
                        <th
                          key={i}
                          className="px-3 py-2 text-left font-medium text-glass-text border-b border-glass"
                        >
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {csvPreviewRows.length === 0 ? (
                      <tr>
                        <td
                          colSpan={Math.max(csvHeaders.length, 1)}
                          className="px-3 py-2 text-glass-text-3"
                        >
                          No data rows after header.
                        </td>
                      </tr>
                    ) : (
                      csvPreviewRows.map((row, ri) => (
                        <tr key={ri} className="border-t border-glass">
                          {csvHeaders.map((_, ci) => (
                            <td
                              key={ci}
                              className="px-3 py-2 text-glass-text-2 whitespace-nowrap max-w-[200px] truncate"
                              title={row[ci] ?? ''}
                            >
                              {row[ci] ?? ''}
                            </td>
                          ))}
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {importText.trim() && csvHeaders.length === 0 && (
            <p className="text-sm text-amber-500 dark:text-amber-300">
              Could not parse columns — check CSV format.
            </p>
          )}

          {importing && (
            <div>
              <div className="flex justify-between text-xs text-glass-text-2 mb-1">
                <span>Importing…</span>
              </div>
              <div className="w-full h-2 bg-glass-3 rounded-full overflow-hidden">
                <div
                  className="h-full w-full bg-accent2 rounded-full origin-left animate-pulse"
                  style={{ animationDuration: '1.2s' }}
                />
              </div>
            </div>
          )}

          {importResults && (
            <div className="rounded-xl border border-glass bg-glass-2 p-3 text-sm space-y-2">
              <div className="font-medium text-glass-text">Import results</div>
              <p className="text-glass-text-2">
                <span className="text-emerald-500 dark:text-emerald-300 font-medium">
                  {importResults.created}
                </span>{' '}
                imported
                {importResults.total > importResults.created && (
                  <>
                    {', '}
                    <span className="text-rose-500 dark:text-rose-300 font-medium">
                      {importResults.total - importResults.created}
                    </span>{' '}
                    failed
                  </>
                )}
                <span className="text-glass-text-3"> (of {importResults.total} rows)</span>
              </p>
              {importResults.errors.length > 0 && (
                <div>
                  <div className="text-xs font-medium text-glass-text-2 mb-1">Errors</div>
                  <ul className="max-h-40 overflow-y-auto text-xs font-mono space-y-1 text-rose-500 dark:text-rose-300 bg-rose-500/5 rounded p-2 border border-rose-500/20">
                    {importResults.errors.map((err, i) => (
                      <li key={i}>
                        Row {err.row}: {err.error || 'Unknown error'}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
        </div>
      </Modal>
    </div>
  );
}
